import { requestSalonSpeakTtsAudio } from "../../api/salonSpeak";

const POLITENESS_MARKERS = [
  "please",
  "would you like",
  "could you",
  "thank you",
  "thanks",
  "okay",
];

const PRIMARY_SPEECH_LANG = "en-CA";
const FALLBACK_SPEECH_LANG = "en-US";
const OPENAI_VOICE_CHOICES = [
  { voiceURI: "", label: "Auto female (OpenAI)", voice: "", name: "Auto female" },
  { voiceURI: "nova", label: "Nova (OpenAI)", voice: "nova", name: "Nova" },
  { voiceURI: "shimmer", label: "Shimmer (OpenAI)", voice: "shimmer", name: "Shimmer" },
  { voiceURI: "coral", label: "Coral (OpenAI)", voice: "coral", name: "Coral" },
  { voiceURI: "sage", label: "Sage (OpenAI)", voice: "sage", name: "Sage" },
  { voiceURI: "alloy", label: "Alloy (OpenAI)", voice: "alloy", name: "Alloy" },
  { voiceURI: "ballad", label: "Ballad (OpenAI)", voice: "ballad", name: "Ballad" },
  { voiceURI: "ash", label: "Ash (OpenAI)", voice: "ash", name: "Ash" },
  { voiceURI: "echo", label: "Echo (OpenAI)", voice: "echo", name: "Echo" },
  { voiceURI: "fable", label: "Fable (OpenAI)", voice: "fable", name: "Fable" },
  { voiceURI: "onyx", label: "Onyx (OpenAI)", voice: "onyx", name: "Onyx" },
];
let preferredVoiceUri = "";
let aiPlaybackAudio = null;
let aiPlaybackObjectUrl = "";

const normalizeWords = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

export const extractImportantWords = (value = "") => {
  const words = normalizeWords(value);
  const stopWords = new Set([
    "the",
    "a",
    "an",
    "to",
    "for",
    "and",
    "or",
    "is",
    "are",
    "of",
    "we",
    "you",
    "i",
    "it",
  ]);
  return Array.from(new Set(words.filter((word) => !stopWords.has(word))));
};

export const scoreAttempt = ({
  transcript,
  importantWords = [],
  attemptRecorded = true,
}) => {
  const transcriptWords = normalizeWords(transcript).join(" ");
  const normalizedImportant = importantWords
    .map((item) => String(item || "").trim().toLowerCase())
    .filter(Boolean);

  const keywordsHit = normalizedImportant.filter((keyword) =>
    transcriptWords.includes(keyword)
  );
  const totalImportantWords = Math.max(normalizedImportant.length, 1);
  const matchedWords = keywordsHit.length;

  const transcriptScore = Math.round((matchedWords / totalImportantWords) * 70);
  const politenessFound = POLITENESS_MARKERS.some((marker) =>
    transcriptWords.includes(marker)
  );
  const politenessScore = politenessFound ? 15 : 0;
  const completionScore = attemptRecorded ? 15 : 0;

  const total = Math.min(100, transcriptScore + politenessScore + completionScore);
  const suggestions = normalizedImportant
    .filter((keyword) => !keywordsHit.includes(keyword))
    .slice(0, 5)
    .map((keyword) => `Try including: ${keyword}`);

  return {
    total,
    transcriptScore,
    politenessScore,
    completionScore,
    politenessFound,
    keywordsHit,
    suggestions,
  };
};

const normalizeLangTag = (value) => String(value || "").trim().toLowerCase();

const resolveSpeakOptions = (rateOrOptions, maybeOptions) => {
  if (rateOrOptions && typeof rateOrOptions === "object") {
    const merged = {
      ...rateOrOptions,
      ...(maybeOptions && typeof maybeOptions === "object" ? maybeOptions : {}),
    };
    return {
      rate: Number(merged.rate ?? 1.0),
      lang: String(merged.lang || "").trim(),
    };
  }
  return {
    rate: Number(rateOrOptions ?? 1.0),
    lang: String(maybeOptions?.lang || "").trim(),
  };
};

const clearAiPlayback = () => {
  if (aiPlaybackAudio) {
    aiPlaybackAudio.pause();
    aiPlaybackAudio = null;
  }
  if (aiPlaybackObjectUrl && typeof URL !== "undefined") {
    URL.revokeObjectURL(aiPlaybackObjectUrl);
    aiPlaybackObjectUrl = "";
  }
};

const playAiBlob = async (blob) => {
  if (
    typeof window === "undefined" ||
    typeof Audio === "undefined" ||
    typeof URL === "undefined" ||
    !blob
  ) {
    return false;
  }

  try {
    clearAiPlayback();
    window.speechSynthesis?.cancel?.();
    aiPlaybackObjectUrl = URL.createObjectURL(blob);
    const audio = new Audio(aiPlaybackObjectUrl);
    aiPlaybackAudio = audio;
    audio.onended = clearAiPlayback;
    audio.onerror = clearAiPlayback;
    await audio.play();
    return true;
  } catch (err) {
    clearAiPlayback();
    return false;
  }
};

const getVoiceChoice = (voiceUri) => {
  const normalized = String(voiceUri || "").trim().toLowerCase();
  return (
    OPENAI_VOICE_CHOICES.find((choice) => choice.voice === normalized) ||
    OPENAI_VOICE_CHOICES[0]
  );
};

const resolveRequestedVoice = () => {
  const choice = getVoiceChoice(preferredVoiceUri);
  return choice.voice || undefined;
};

const fallbackBrowserSpeak = (phrase, { rate, lang }) => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return false;
  }
  const utterance = new SpeechSynthesisUtterance(phrase);
  utterance.lang = lang || PRIMARY_SPEECH_LANG;
  utterance.rate = Math.min(1.25, Math.max(0.7, Number(rate || 1) * 1.05));
  utterance.pitch = 1.1;
  utterance.volume = 1;
  clearAiPlayback();
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
  return true;
};

const requestAndPlayAiSpeech = async (phrase, { rate, lang, voice }) => {
  const blob = await requestSalonSpeakTtsAudio({
    text: phrase,
    lang,
    rate: Number(rate || 1),
    voice,
  });
  return playAiBlob(blob);
};

const resolveRecognitionLang = () => {
  return PRIMARY_SPEECH_LANG || FALLBACK_SPEECH_LANG;
};

export const setSalonSpeakVoicePreference = (voiceUri) => {
  preferredVoiceUri = String(voiceUri || "").trim().toLowerCase();
};

export const listSalonSpeakVoiceChoices = () => OPENAI_VOICE_CHOICES;

export const isSalonSpeakVoiceChoice = (voiceUri) =>
  OPENAI_VOICE_CHOICES.some((choice) => choice.voiceURI === String(voiceUri || "").trim().toLowerCase());

export const speakText = async (text, rateOrOptions = 1.0, maybeOptions = {}) => {
  const phrase = String(text || "").trim();
  if (!phrase) return false;
  const { rate, lang } = resolveSpeakOptions(rateOrOptions, maybeOptions);
  const normalizedLang = normalizeLangTag(lang) || PRIMARY_SPEECH_LANG.toLowerCase();

  try {
    const played = await requestAndPlayAiSpeech(phrase, {
      rate,
      lang: normalizedLang,
      voice: normalizedLang.startsWith("en") ? resolveRequestedVoice() : undefined,
    });
    if (played) return true;
  } catch (err) {
    // Fall back to browser speech only when AI TTS is unavailable.
  }

  return fallbackBrowserSpeak(phrase, { rate, lang: lang || PRIMARY_SPEECH_LANG });
};

export const speakVietnameseText = async (text, rate = 1.0) => {
  const phrase = String(text || "").trim();
  if (!phrase) return false;
  return speakText(phrase, { rate, lang: "vi-VN" });
};

const getSpeechRecognitionClass = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const canRecognizeSpeech = () => Boolean(getSpeechRecognitionClass());

export const canRecordAudio = () =>
  typeof window !== "undefined" &&
  Boolean(window.MediaRecorder) &&
  Boolean(navigator?.mediaDevices?.getUserMedia);

export const chooseBranchTurn = (nextMap = {}, transcript = "", fallback = null) => {
  const normalized = String(transcript || "").toLowerCase();
  const entries = Object.entries(nextMap || {});
  for (const [keyword, index] of entries) {
    if (keyword === "default") continue;
    if (normalized.includes(String(keyword).toLowerCase())) {
      return Number(index);
    }
  }
  if (Object.prototype.hasOwnProperty.call(nextMap || {}, "default")) {
    return Number(nextMap.default);
  }
  return fallback;
};

export async function startVoiceCapture({
  salonMode = false,
  durationMs = 5500,
  onTranscript,
  onComplete,
  onError,
  forceMediaRecorder = false,
}) {
  const SpeechRecognition = getSpeechRecognitionClass();

  if (SpeechRecognition && (!forceMediaRecorder || !canRecordAudio())) {
    const recognition = new SpeechRecognition();
    recognition.lang = resolveRecognitionLang();
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    let finalized = false;
    let transcript = "";

    const finalize = () => {
      if (finalized) return;
      finalized = true;
      onComplete?.({ transcript: transcript.trim() || null, source: "recognition" });
    };

    recognition.onresult = (event) => {
      const chunk = Array.from(event.results || [])
        .map((result) => result?.[0]?.transcript || "")
        .join(" ")
        .trim();
      transcript = chunk || transcript;
      onTranscript?.(transcript);
    };

    recognition.onerror = (event) => {
      onError?.(event?.error || "Speech recognition failed");
    };

    recognition.onend = () => {
      finalize();
    };

    recognition.start();
    const timer = setTimeout(() => {
      try {
        recognition.stop();
      } catch (err) {
        finalize();
      }
    }, durationMs);

    return {
      stop: () => {
        clearTimeout(timer);
        try {
          recognition.stop();
        } catch (err) {
          finalize();
        }
      },
      source: "recognition",
    };
  }

  if (!canRecordAudio()) {
    throw new Error("Audio not supported");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      noiseSuppression: Boolean(salonMode),
      autoGainControl: Boolean(salonMode),
      echoCancellation: true,
    },
  });

  if (salonMode) {
    const track = stream.getAudioTracks()?.[0];
    if (track?.applyConstraints) {
      track
        .applyConstraints({
          noiseSuppression: true,
          autoGainControl: true,
        })
        .catch(() => null);
    }
  }

  const mimeTypeCandidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
  ];
  const supportedMimeType = mimeTypeCandidates.find((type) =>
    typeof MediaRecorder !== "undefined" &&
    typeof MediaRecorder.isTypeSupported === "function" &&
    MediaRecorder.isTypeSupported(type)
  );
  const recorder = supportedMimeType
    ? new MediaRecorder(stream, { mimeType: supportedMimeType })
    : new MediaRecorder(stream);
  const audioChunks = [];
  recorder.ondataavailable = (event) => {
    if (event?.data?.size) {
      audioChunks.push(event.data);
    }
  };

  let finalized = false;
  const finalize = () => {
    if (finalized) return;
    finalized = true;
    stream.getTracks().forEach((track) => track.stop());
    const mimeType = recorder.mimeType || supportedMimeType || "audio/webm";
    const audioBlob = audioChunks.length ? new Blob(audioChunks, { type: mimeType }) : null;
    onComplete?.({ transcript: null, source: "media", audioBlob });
  };

  recorder.onerror = () => {
    onError?.("Recording failed");
    finalize();
  };
  recorder.onstop = () => {
    setTimeout(finalize, 0);
  };

  recorder.start(250);
  const stopRecorder = () => {
    if (recorder.state === "inactive") {
      finalize();
      return;
    }
    try {
      if (typeof recorder.requestData === "function") {
        recorder.requestData();
      }
      recorder.stop();
    } catch (err) {
      finalize();
    }
  };
  const timer = setTimeout(() => {
    stopRecorder();
  }, durationMs);

  return {
    stop: () => {
      clearTimeout(timer);
      stopRecorder();
    },
    source: "media",
  };
}
